import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  listProductTypes,
  createProductType,
  updateProductType,
  deleteProductType,
} from "../../api/productTypes";

const SEED_CODES = ["ACCOMMODATION", "CATERING", "TUITION", "ACTIVITY", "TRIP", "STAFF", "TRANSPORT"];

const blank = () => ({
  code: "",
  name: "",
  displayOrder: 0,
  isActive: true,
});

export default function ProductTypesAdmin() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(() => {
    setLoading(true);
    setError("");
    listProductTypes()
      .then((d) => setRows(Array.isArray(d) ? d : []))
      .catch((e) => setError(e.message || "Failed to load product types"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openNew = () => { setEditing(blank()); setEditingId(null); };
  const openEdit = (row) => {
    setEditing({
      code: row.code || "",
      name: row.name || "",
      displayOrder: row.displayOrder ?? 0,
      isActive: row.isActive !== false,
    });
    setEditingId(row.id);
  };
  const close = () => { setEditing(null); setEditingId(null); };

  const save = async () => {
    if (!editing.code.trim() || !editing.name.trim()) {
      setError("Code and name are required");
      return;
    }
    const body = {
      code: editing.code.trim().toUpperCase(),
      name: editing.name.trim(),
      displayOrder: Number(editing.displayOrder) || 0,
      isActive: !!editing.isActive,
    };
    try {
      setSaving(true);
      if (editingId) await updateProductType(editingId, body);
      else await createProductType(body);
      close();
      fetchAll();
    } catch (e) {
      setError(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Soft-delete product type "${row.name}"?`)) return;
    try {
      await deleteProductType(row.id);
      fetchAll();
    } catch (e) {
      setError(e.message || "Delete failed");
    }
  };

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>Product Types</Typography>
        <Button startIcon={<RefreshIcon />} onClick={fetchAll}>Refresh</Button>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>New Type</Button>
      </Stack>

      <Alert severity="info" sx={{ mb: 2 }}>
        The seed codes ({SEED_CODES.join(", ")}) drive the engine's auto-apply rules.
        Adding new product types isn't supported by the engine — they'll only appear if placed manually in the day grid.
      </Alert>

      {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Display Order</TableCell>
              <TableCell>Active</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} align="center"><CircularProgress size={24} /></TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={5} align="center">No product types</TableCell></TableRow>
            ) : rows.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>{row.code}</TableCell>
                <TableCell>{row.name}</TableCell>
                <TableCell>{row.displayOrder}</TableCell>
                <TableCell>{row.isActive ? "Yes" : "No"}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => openEdit(row)}><EditIcon fontSize="small" /></IconButton>
                  <IconButton size="small" onClick={() => remove(row)}><DeleteIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={editing !== null} onClose={close} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? "Edit Product Type" : "New Product Type"}</DialogTitle>
        <DialogContent>
          {editing && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Code" required fullWidth
                value={editing.code}
                onChange={(e) => setEditing({ ...editing, code: e.target.value })}
                helperText="Uppercase identifier, e.g. ACTIVITY"
              />
              <TextField
                label="Name" required fullWidth
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
              />
              <TextField
                label="Display Order" type="number"
                value={editing.displayOrder}
                onChange={(e) => setEditing({ ...editing, displayOrder: e.target.value })}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!editing.isActive}
                    onChange={(e) => setEditing({ ...editing, isActive: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
